import api from '@common/api';
import session from '@common/session';
import { getDayNameByNumber } from '@common/utility/datetime';
import numbers from '@common/utility/numbers';
import { FrequencyMap, WeekdayMap } from '@services/appsync/mappings';
import { AutoInvestScheduleV2 } from '@services/appsync/types';
import { createStore, useStore } from 'zustand';
import { persist } from 'zustand/middleware';
import { AsyncResult } from './types';

interface AutoInvestmentsModelProps {
  autoInvestments: AutoInvestScheduleV2[];
}

interface AutoInvestmentsModelActions {
  deleteAutoInvestment: (id: string) => Promise<AsyncResult>;
  getFrequencyDescription: (props: {
    frequency: FrequencyMap;
    daysOfMonth: number[] | null;
    dayOfWeek: number | null;
  }) => string;
  fetchAutoInvestments: () => Promise<AsyncResult<AutoInvestScheduleV2[]>>;
  setAutoInvestments: (autoInvestments: AutoInvestScheduleV2[]) => void;
  getAutoInvest: (id: string) => Promise<AsyncResult<AutoInvestScheduleV2>>;
}

export const AutoInvestmentsModel = createStore(
  persist<AutoInvestmentsModelProps & AutoInvestmentsModelActions>(
    (set, get) => ({
      autoInvestments: [],
      deleteAutoInvestment: async (id) => {
        const investorID = session.get()?.investorId;
        if (!investorID) return { error: 'No investor ID', success: false };
        try {
          const result = await api.deleteAutoInvestSchedule({ autoinvest_id: id, investor_account_id: investorID });
          return { success: result };
        } catch {
          return { error: 'Unable to get bank accounts', success: false };
        }
      },
      fetchAutoInvestments: async () => {
        const investorID = session.get()?.investorId;
        if (!investorID) return { error: 'No investor ID', success: false };

        try {
          const autoInvestments = await api.allAutoInvestSchedules({ investor_account_id: investorID });
          set({ autoInvestments });
          return { data: autoInvestments, success: true };
        } catch {
          return { error: 'Unable to get bank accounts', success: false };
        }
      },
      getAutoInvest: async (id) => {
        let autoInvests: AutoInvestScheduleV2[] = get().autoInvestments;
        if (autoInvests.length === 0) {
          autoInvests = (await get().fetchAutoInvestments()).data ?? [];
        }

        const autoInvest = autoInvests.find((autoInvest) => autoInvest.autoinvest_id === id);

        return { data: autoInvest, success: Boolean(autoInvest) };
      },
      getFrequencyDescription: ({ frequency, daysOfMonth, dayOfWeek }) => {
        switch (frequency) {
          case FrequencyMap.WEEKLY:
            return `Weekly on ${getDayNameByNumber(dayOfWeek ? dayOfWeek : WeekdayMap.MONDAY)}s`;
          case FrequencyMap.MONTHLY:
            if (daysOfMonth && daysOfMonth.length > 0) {
              return `Monthly on the ${numbers.getOrdinalName(daysOfMonth[0] ?? 1)}${daysOfMonth
                .slice(1)
                .map((date) => ` and ${numbers.getOrdinalName(date)}`)}`;
            } else {
              return 'Monthly';
            }
          case FrequencyMap.ONE:
            return 'One-time';
          case FrequencyMap.DAILY:
            return 'Daily';
          case FrequencyMap.BI_MONTHLY:
            return '1st and 15th of the month';
          default:
            return 'One-time';
        }
      },
      setAutoInvestments: (autoInvestments) => {
        set({ autoInvestments });
      },
    }),
    {
      getStorage: () => localStorage,
      name: 'auto-investments-model',
    },
  ),
);

export function useAutoInvestmentsModel(): AutoInvestmentsModelProps & AutoInvestmentsModelActions;
export function useAutoInvestmentsModel<T>(
  selector: (state: AutoInvestmentsModelProps & AutoInvestmentsModelActions) => T,
  equals?: (a: T, b: T) => boolean,
): T;
export function useAutoInvestmentsModel<T>(
  selector?: (state: AutoInvestmentsModelProps & AutoInvestmentsModelActions) => T,
  equals?: (a: T, b: T) => boolean,
) {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return useStore(AutoInvestmentsModel, selector!, equals);
}
